import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AlertController, ToastController } from '@ionic/angular';
import { Subscription } from 'rxjs';

import { TASK_DELIVERY_PORT, TaskDeliveryPort } from './task-delivery.port';
import {
  TASK_ACCEPTED_MIME_TYPES,
  TASK_FILE_ACCEPT,
  TaskConstraints,
  TaskData,
  TaskFileKind,
  TaskResource,
  TaskSavedEvent,
  TaskSection,
  TaskSubmitResponse,
  taskFileKind,
  taskFileSize,
  taskStatusLabel,
} from './task-delivery.model';
import { ToastService } from 'src/app/service/toast-service/toast.service';

type TaskDeliveryPhase = 'loading' | 'error' | 'ready' | 'sending' | 'sent';

const BYTES_PER_MB = 1024 * 1024;

/** Production's own copy, kept word for word where it is the same screen. */
const SEND_ERROR = 'No pudimos enviar tu tarea. Inténtalo nuevamente.';
const LOAD_ERROR = 'No pudimos cargar la tarea. Inténtalo nuevamente.';

/** Estados de la tarea (los del backend) en los que el alumno puede adjuntar y enviar. */
const EDITABLE_STATUSES: readonly string[] = ['pending', 'observed'];

/**
 * Entrega de una tarea (`project`), extraída de la copia que `pages/tema` repite en cuatro páginas
 * (`tema.page.html:1082-1255` + `tema.page.ts:6022-6240`). Es la **primera vez** que vive en un
 * componente: las copias de producción no se tocan (decisión de la dueña), así que este componente
 * solo lo consume la ruta por ahora.
 *
 * Qué es de este componente y qué no:
 * - **Sí**: las cuatro secciones del acordeón, la validación de cantidad / tamaño / tipo, armar el
 *   `FormData` con el mismo shape que producción, la descarga de recursos (con la misma confirmación
 *   de Wi-Fi), y las fases de carga → listo → enviando → enviado.
 * - **No**: de dónde sale ni adónde va el dato. Eso es el {@link TaskDeliveryPort}, inyectado con
 *   `TASK_DELIVERY_PORT`. Por defecto habla con `ApiService`; la ruta provee el suyo, que pasa por
 *   `LearningPathService`. Así el componente nunca decide por su cuenta una llamada de la ruta.
 *
 * Diferencias a propósito con producción:
 * - Un error al enviar se muestra **dentro** del componente y conserva los archivos. Producción cierra
 *   el modal en cualquier caso, y en la ruta no hay modal que cerrar.
 * - Tras enviar se vuelve a pedir la tarea, para que los archivos aparezcan como ya guardados.
 * - Si el port ofrece borrador (`restoreDraft` / `saveDraft` / `clearDraft`), la tarea y los archivos
 *   elegidos sobreviven a que quien monta el componente lo destruya y lo vuelva a crear. Sin él, todo
 *   queda en los campos del componente, como en producción.
 *
 * `variant` cambia la presentación, nunca el protocolo:
 * - `inline`: el acordeón de producción en una tarjeta con relleno, sin chrome alrededor.
 * - `modal`: el mismo acordeón sin relleno, porque el marco del modal ya lo pone. Es para una futura
 *   migración de producción.
 * - `card`: el diseño de dos columnas — encabezado con título e indicaciones, «Tu entrega» con zona
 *   de arrastre y «Recursos de la tarea» al lado (apilados en móvil). Es una tarjeta sola: el fondo
 *   que la rodea (la trama, el color) lo pone quien la monta, igual que un modal pone el suyo.
 *   Lleva `title` y `typeLabel` por `@Input`, porque la tarea en sí no trae título.
 */
@Component({
  selector: 'app-task-delivery',
  templateUrl: './task-delivery.component.html',
  styleUrls: ['./task-delivery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskDeliveryComponent implements OnInit, OnDestroy {
  /** Optional subtitle, as production's modal shows the course name under its title. */
  @Input() courseName = '';
  @Input() variant: 'modal' | 'inline' | 'card' = 'inline';
  /** `card` only: the heading. The task payload has no title of its own. */
  @Input() title = '';
  /** `card` only: the kind chip above the heading («Tarea»). */
  @Input() typeLabel = '';
  /**
   * `card` only: no card chrome (white background, shadow, corners), so the content sits straight on
   * the host's surface. Off by default, so the card looks as it always did.
   */
  @Input() bleed = false;

  /** Fires once the task is sent. The data is already re-requested by the time it does. */
  @Output() saved = new EventEmitter<TaskSavedEvent>();

  @HostBinding('class.task-delivery--inline')
  get isInline(): boolean {
    return this.variant === 'inline';
  }

  @HostBinding('class.task-delivery--card')
  get isCard(): boolean {
    return this.variant === 'card';
  }

  @Input()
  set projectId(value: number) {
    if (value === this._projectId) return;
    this._projectId = value;
    // The first load waits for `ngOnInit`: a draft is only valid for the `status` it was saved with,
    // and inputs are bound in template order, so `status` has not arrived yet when this runs first.
    if (this.initialised) this.load();
  }
  get projectId(): number {
    return this._projectId;
  }
  private _projectId!: number;

  /**
   * Estado de la tarea según el backend (`pending`, `in_review`, `observed`, `disapproved`, `passed`).
   * Solo `pending` y `observed` dejan adjuntar y enviar. `null` = quien monta el componente no lo
   * sabe; entonces se infiere de los archivos guardados.
   */
  @Input()
  set status(value: string | null) {
    if (value === this._status) return;
    this._status = value;
    // Si el nuevo estado vuelve a permitir el envío (p. ej. `observed`), el "ya enviado" de esta
    // sesión deja de mandar. Con `in_review` se queda, junto con el mensaje del backend.
    if (this.phase === 'sent' && (value === null || EDITABLE_STATUSES.includes(value))) {
      this.phase = 'ready';
      this.successMsg = '';
    }
  }
  get status(): string | null {
    return this._status;
  }
  private _status: string | null = null;

  phase: TaskDeliveryPhase = 'loading';
  task: TaskData | null = null;
  /** The learner's files, reference-free: production keeps this list inside `taskData` itself. */
  userResources: TaskResource[] = [];
  openSection: TaskSection | null = 'indicaciones';
  /** Estado de la tarea según el último envío. Vacío hasta que el backend lo informe. */
  statusLabel: string | null = null;
  /** `card` only: a file is being dragged over the drop zone. */
  dragging = false;
  /** Error de envío o de carga, mostrado en línea. */
  errorMsg = '';
  /** Mensaje del backend tras un envío exitoso. */
  successMsg = '';

  private initialised = false;
  private loadSub?: Subscription;
  private submitSub?: Subscription;

  constructor(
    @Inject(TASK_DELIVERY_PORT) private port: TaskDeliveryPort,
    private toastCtrl: ToastController,
    private alertCtrl: AlertController,
    private changeDetector: ChangeDetectorRef,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.initialised = true;
    if (this._projectId !== undefined) this.load();
  }

  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
    this.submitSub?.unsubscribe();
  }

  // ── Carga ───────────────────────────────────────────────────────────────

  load(): void {
    this.loadSub?.unsubscribe();
    this.errorMsg = '';
    this.successMsg = '';

    if (this.restoreDraft()) return;

    this.phase = 'loading';

    this.loadSub = this.port.getTask(this._projectId).subscribe({
      next: (task) => {
        this.applyTask(task);
        this.phase = 'ready';
        this.persistDraft();
        this.changeDetector.markForCheck();
      },
      error: () => {
        this.task = null;
        this.phase = 'error';
        this.errorMsg = LOAD_ERROR;
        this.changeDetector.markForCheck();
      },
    });
  }

  /**
   * Re-reads the task after a send without touching the phase, so the success message stays on
   * screen while the learner's files switch from "to send" to "stored". A failure here is silent:
   * the send already succeeded, and the list simply keeps what was on screen.
   */
  private refreshTask(): void {
    this.loadSub?.unsubscribe();
    this.loadSub = this.port.getTask(this._projectId).subscribe({
      next: (task) => {
        this.applyTask(task);
        this.persistDraft();
        this.changeDetector.markForCheck();
      },
    });
  }

  private applyTask(task: TaskData): void {
    this.task = task;
    this.userResources = [...(task.user_resources ?? [])];
  }

  // ── Borrador ────────────────────────────────────────────────────────────

  /**
   * Takes the port's draft instead of asking for the task again. Only valid for the `status` it was
   * saved with: the task moving on means its payload (feedback, stored files) is worth reading again.
   * Returns whether it was used.
   */
  private restoreDraft(): boolean {
    const draft = this.port.restoreDraft?.(this._projectId);
    if (!draft || draft.status !== this._status) return false;

    this.task = draft.task;
    this.userResources = [...draft.userResources];
    this.openSection = draft.openSection;
    this.phase = 'ready';
    return true;
  }

  /** Called after every change the learner or the backend makes to what is on screen. */
  private persistDraft(): void {
    if (!this.task) return;
    this.port.saveDraft?.(this._projectId, {
      task: this.task,
      userResources: this.userResources,
      openSection: this.openSection,
      status: this._status,
    });
  }

  // ── Presentación ────────────────────────────────────────────────────────

  get constraints(): TaskConstraints | null {
    return this.task?.constraints ?? null;
  }

  /** Grading is optional: a reviewed task can come without a grade. */
  get hasGrade(): boolean {
    return this.task?.grade !== null && this.task?.grade !== undefined;
  }

  /** The upload control exists only when the task says how many and how big. */
  get canUpload(): boolean {
    return this.constraints !== null;
  }

  /**
   * The task is closed to the learner: neither attaching nor sending. The backend's `status` decides
   * (only `pending` / `observed` are open); `phase === 'sent'` bridges the gap between a send and the
   * refresh that brings the new status. Without a status, stored files stand in for it.
   */
  get isDelivered(): boolean {
    if (this.phase === 'sent') return true;
    if (this._status !== null) return !EDITABLE_STATUSES.includes(this._status);
    return this.userResources.some((resource) => this.isStored(resource));
  }

  /** `card`: the supervisor asked for changes and the learner can still answer. */
  get isObserved(): boolean {
    return this._status === 'observed' && !this.isDelivered;
  }

  /** Files picked and not yet sent: the only thing "Enviar" has to send. */
  get hasPendingFiles(): boolean {
    return this.userResources.some((resource) => resource.type_resource === 'file' && !!resource.upload);
  }

  get canAddFile(): boolean {
    const max = this.constraints?.max_quantity_upload_files ?? 0;
    return !this.isDelivered && this.userResources.length < max;
  }

  get canSend(): boolean {
    return this.phase === 'ready' && !this.isDelivered && this.hasPendingFiles;
  }

  get isSending(): boolean {
    return this.phase === 'sending';
  }

  /**
   * `card`: the state chip next to the kind chip. Right after a send it shows what the backend
   * answered; otherwise the item's own state, which is what a later refresh keeps current.
   */
  get statusChip(): string | null {
    if (this.phase === 'sent' && this.statusLabel) return this.statusLabel;
    return taskStatusLabel(this._status) ?? this.statusLabel;
  }

  /** `card`: the note beside «Tu entrega». Empty once the task is delivered. */
  get deliveryHint(): string {
    if (this.isDelivered) return '';
    if (!this.hasPendingFiles) return 'Sube al menos un archivo para poder enviar.';
    return `${this.userResources.length} de ${this.constraints?.max_quantity_upload_files ?? 0} archivos`;
  }

  /** `card`: what the drop zone accepts, from the task's own constraints. */
  get dropzoneRules(): string {
    const constraints = this.constraints;
    if (!constraints) return '';
    return `PDF, imágenes, video u Office · hasta ${constraints.max_size_upload_files} MB cada uno · máx. ${constraints.max_quantity_upload_files}`;
  }

  /** `card`: the drop zone takes files only while the task is open and there is room. */
  get dropzoneEnabled(): boolean {
    return this.canAddFile && !this.isSending;
  }

  readonly acceptTypes = TASK_FILE_ACCEPT;

  kindOf(resource: TaskResource): TaskFileKind {
    return taskFileKind(resource);
  }

  fileSize(resource: TaskResource): string {
    return taskFileSize(resource.size);
  }

  toggleSection(section: TaskSection): void {
    this.openSection = this.openSection === section ? null : section;
    this.persistDraft();
  }

  isOpen(section: TaskSection): boolean {
    return this.openSection === section;
  }

  sizeMb(resource: TaskResource): string {
    return ((resource.size ?? 0) / BYTES_PER_MB).toFixed(2);
  }

  resourceName(resource: TaskResource): string {
    return resource.name || resource.title || '';
  }

  isStored(resource: TaskResource): boolean {
    return resource.type_resource === 'media';
  }

  // ── Archivos ────────────────────────────────────────────────────────────

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    this.addFiles(files);
  }

  onDragOver(event: DragEvent): void {
    if (!this.dropzoneEnabled) return;
    // Without this the browser refuses the drop and opens the file instead.
    event.preventDefault();
    this.dragging = true;
  }

  onDragLeave(event: DragEvent): void {
    // Moving over a child of the zone also fires `dragleave`; only leaving the zone itself counts.
    const zone = event.currentTarget as HTMLElement;
    if (event.relatedTarget instanceof Node && zone.contains(event.relatedTarget)) return;
    this.dragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragging = false;
    if (!this.dropzoneEnabled) return;
    this.addFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  /**
   * The one door every picked or dropped file goes through, so both obey the same limits. Each file is
   * checked on its own; the learner is told about the first one refused, not about every one.
   */
  private addFiles(files: File[]): void {
    if (!files.length || !this.constraints || !this.canAddFile) return;

    this.openSection = 'documentos';

    const { max_quantity_upload_files: maxCount, max_size_upload_files: maxSizeMb } = this.constraints;
    let kept = this.userResources;
    let refusal = '';

    for (const file of files) {
      if (kept.length >= maxCount) {
        refusal ||= `Solo puedes subir hasta ${maxCount} archivos.`;
        break;
      }
      if (file.size / BYTES_PER_MB > maxSizeMb) {
        refusal ||= `El limite máximo por archivo es de ${maxSizeMb} MB.`;
        continue;
      }
      if (!TASK_ACCEPTED_MIME_TYPES.has(file.type)) {
        refusal ||= `Recuerda que solo puede subir archivos de tipo: imagen, video, pdf y office.`;
        continue;
      }
      kept = [...kept, { name: file.name, size: file.size, type_resource: 'file', upload: file }];
    }

    if (refusal) this.toastService.presentToast("error", refusal, 3000, "top", false);

    if (kept !== this.userResources) {
      this.userResources = kept;
    }
    this.persistDraft();
    this.changeDetector.markForCheck();
  }

  removeResource(index: number): void {
    if (this.isSending || this.isDelivered) return;
    this.userResources = this.userResources.filter((_, i) => i !== index);
    this.persistDraft();
    this.changeDetector.markForCheck();
  }

  // ── Descarga ────────────────────────────────────────────────────────────

  /** Same confirmation production shows before any download. */
  async confirmDownload(resource: TaskResource): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: 'Descargar',
      message: 'Te sugerimos utilizar una red WI-FI',
      mode: 'ios',
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        { text: 'Continuar', handler: () => this.port.downloadResource(resource) },
      ],
    });
    await alert.present();
  }

  // ── Envío ───────────────────────────────────────────────────────────────

  send(): void {
    if (!this.canSend) return;

    this.phase = 'sending';
    this.errorMsg = '';
    this.successMsg = '';
    this.submitSub?.unsubscribe();

    this.submitSub = this.port.submitTask(this._projectId, this.buildFormData()).subscribe({
      next: (response) => this.onSubmitted(response),
      error: () => this.onSubmitFailed(),
    });
  }

  /**
   * Same shape production sends: picked files under `files[]`, stored media under
   * `multimedias[i][key]` with every key the resource carries.
   */
  private buildFormData(): FormData {
    const formData = new FormData();

    this.userResources
      .filter((resource) => resource.type_resource === 'file' && resource.upload)
      .forEach((resource) => formData.append('files[]', resource.upload as File));

    this.userResources
      .filter((resource) => resource.type_resource === 'media')
      .forEach((media, index) => {
        Object.keys(media).forEach((key) => {
          formData.append(`multimedias[${index}][${key}]`, String(media[key]));
        });
      });

    return formData;
  }

  private onSubmitted(response: TaskSubmitResponse): void {
    if (response?.type !== 'success') {
      this.onSubmitFailed();
      return;
    }

    const data = response.data ?? {};
    this.successMsg = data.msg ?? '';
    this.statusLabel = data.status_label ?? this.statusLabel;
    this.phase = 'sent';
    // What was picked is sent now; the re-read below saves the stored files in its place.
    this.port.clearDraft?.(this._projectId);
    this.saved.emit({
      msg: this.successMsg,
      status_label: data.status_label ?? null,
      status_code: data.status_code ?? null,
    });

    // Lo que quedó guardado reemplaza a lo que se acaba de elegir.
    this.refreshTask();
  }

  private onSubmitFailed(): void {
    this.phase = 'ready';
    this.errorMsg = SEND_ERROR;
    this.changeDetector.markForCheck();
  }

}
